import {Component, effect, inject, input, resource, ResourceStatus, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {LeadResources} from '../../../../resources/lead.resources';
import {SubscriptionContextV2Service} from '../../../../services/subscription-v2-context.service';
import {Lead} from '../../../../data/domain/lead';
import {DropdownComponent} from '../../common/form/dropdown/dropdown.component';
import {AgeRangeSliderComponent} from '../../common/age-range-slider/age-range-slider.component';
import {NgForOf, NgIf, NgSwitch, NgSwitchCase} from '@angular/common';
import {createOrUpdateLead} from '../../../../common/lead';
import {CheckboxComponent} from '../../common/form/checkbox/checkbox.component';
import {Option, OptionsConfig, SubscriptionOffer, SubscriptionOffersConfig} from '../../../../model/subscription.model';
import {RemoteConfig} from '../../../config/remote-config';
import {SessionResources} from '../../../../resources/session.resources';
import {Router} from '@angular/router';
import {SubNavbarV2Component} from '../sub-navbar-v2/sub-navbar-v2.component';
import {SubscriptionSearchActions} from '../../common/subscription-search/subscription-search.actions';
import {FIREBASE_AUTH} from '../../../config/module.di.tokens';
import {SocialTrackService} from '../../../../services/social-track.service';
import {SessionService} from '../../../../services/session.service';
import {SessionTopic} from '../../../../model/session.model';
import {convertToObject} from '../../../../common/objects';
import {isComplete, isForbiddenAgedClient} from '../../../../common/sessions';
import {SessionDTO} from '@minutespourtoi/api-models/dist/models';
import {toSignal} from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-sub-v2-search',
  imports: [
    ReactiveFormsModule,
    DropdownComponent,
    AgeRangeSliderComponent,
    NgIf,
    CheckboxComponent,
    NgForOf,
    SubNavbarV2Component,
    NgSwitch,
    NgSwitchCase
  ],
  templateUrl: './sub-v2-search.component.html',
})
export class SubV2SearchComponent  {

  @ViewChild(AgeRangeSliderComponent)
  appRangeSlider: AgeRangeSliderComponent

  sessionId = input<string>()
  referralCode = input<string>()
  sessionResources = inject(SessionResources)
  leadResources = inject(LeadResources);
  remoteConfig = inject(RemoteConfig)
  router = inject(Router)
  offers = new Array<SubscriptionOffer>()
  offersConfig: SubscriptionOffersConfig
  optionsConfig: OptionsConfig
  showCommonHobbiesOption: Option
  formBuilder = inject(FormBuilder)
  subscriptionContextV2Service = inject(SubscriptionContextV2Service)
  subscriptionSearchActions = inject(SubscriptionSearchActions)
  socialTrackService = inject(SocialTrackService)
  sessionService = inject(SessionService)
  isComplete = false

  auth = inject(FIREBASE_AUTH)
  topic = resource<SessionTopic, SessionDTO>({
    request: () => this.sessionResources.session.value(),
    loader: async ({ request: session }) => this.sessionService.topicOfSession(session)
  });
  changeOfferUpdate = toSignal(this.subscriptionSearchActions.searchForm.controls.offer.valueChanges)
  changeAgeUpdate = toSignal(this.subscriptionSearchActions.searchForm.controls.age.valueChanges)

  topicSearchForm = new FormGroup({
    options: new FormControl<Array<'FF'|'FB'|'FX'>>([], null)
  })

  constructor() {

    effect(async () => {
      const session = this.sessionResources.session.value()
      const currentLead = this.leadResources.currentLead.value()
      const age = this.changeAgeUpdate()
      if(session && age && currentLead) {
        await this.loadOffers(currentLead)
      }
    });
    effect(async () => {
      const offerCode = this.changeOfferUpdate()
      if(offerCode) {
        await this.updateOfferChange()
      }
    });
    effect(async () => {
      const currentLeadStatus = this.leadResources.currentLead.status()
      const sessionStatus = this.sessionResources.session.status()
      const sessionTopic = this.sessionResources.session.value()?.topic
      if(currentLeadStatus == ResourceStatus.Resolved || sessionStatus == ResourceStatus.Resolved) {
        const currentLead = this.leadResources.currentLead.value()
        if(currentLead) {
          this.topicSearchForm.controls.options.setValue(currentLead.topicSearchOptions ?? [])
          await this.subscriptionSearchActions.loadAges(currentLead, this.referralCode() != null)
          await this.loadOffers(currentLead)
          if(currentLead.offer) {
            this.subscriptionContextV2Service.defaultOffer.set(currentLead.offer)
          }
        }
        if(sessionTopic == "lgbt_w" && (!this.topicSearchForm.controls.options.value || this.topicSearchForm.controls.options.value.length == 0)) {
          this.topicSearchForm.controls.options.setValue(['FX'])
        }
      }
    });

  }

  private async updateOfferChange() {

    const offer = this.offers.find(offerIn => offerIn.code == this.subscriptionSearchActions.searchForm.controls.offer.value)
    if(offer) {
      this.subscriptionContextV2Service.defaultOffer.set(offer)
    }
  }

  async next() {

    if (this.subscriptionSearchActions.searchForm.valid) {
      const leadDTO = await createOrUpdateLead(this.sessionId(), this.leadResources, this.auth)
      leadDTO.age = Number(this.subscriptionSearchActions.searchForm.controls.age.value)
      leadDTO.desiredAgeMin = Number(this.subscriptionSearchActions.searchForm.controls.ageMin.value)
      leadDTO.desiredAgeMax = Number(this.subscriptionSearchActions.searchForm.controls.ageMax.value)
      leadDTO.options = new Array<Option>()
      if(this.subscriptionSearchActions.searchForm.controls.showCommonHobbies.value) {
        leadDTO.options.push(this.showCommonHobbiesOption)
      }
      if(this.topic.value()?.searchOptions?.length > 0) {
        leadDTO.topicSearchOptions = this.topicSearchForm.controls.options.value
      }

      const offer = this.offers.find(offerIn => offerIn.code == this.subscriptionSearchActions.searchForm.controls.offer.value)
      if (offer) {
        leadDTO.offer = offer
      } else {
        leadDTO.offer = this.offers[0]
      }
      let ageCategory = 'y'
      if (leadDTO.age > 40 && leadDTO.age <= 45 ) {
        ageCategory = 'm'
      } else if (leadDTO.age >= 46) {
        ageCategory = 's'
      }
      this.leadResources.saveLeadRequest.set(leadDTO)
      this.subscriptionContextV2Service.age.set(ageCategory)
      this.subscriptionContextV2Service.defaultOffer.set(leadDTO.offer)

      this.socialTrackService.trackGAEvent('sarch_next')

      await this.router.navigate(['/subscribe-v2', this.sessionId(), 'finalize'], {
        queryParams: {
          referralCode: this.referralCode()
        }
      })
    }
  }

  async changeOffer(offerCode: string) {
    this.subscriptionSearchActions.searchForm.controls.offer.setValue(offerCode)
  }

  changeTopicSearchOption(code: 'FF'|'FB'| 'FX') {

    if(this.topicSearchForm.controls.options.value.indexOf(code) == -1) {
      let newValue = [...this.topicSearchForm.controls.options.value, code]
      if(code != 'FX') {
        newValue = newValue.filter(value => value != 'FX')
      } else {
        newValue = ['FX']
      }
      this.topicSearchForm.controls.options.setValue(newValue)
    } else {
      let newOptions = this.topicSearchForm.controls.options.value.filter(option => option != code) as Array<'FF'|'FB'|'FX'>
      if(newOptions.length == 0) {
        newOptions = ['FX']
      }
      this.topicSearchForm.controls.options.setValue(newOptions)
    }

  }

  private async loadOffers(leadDTO: Lead) {
    if (leadDTO) {
      this.offers = new Array<SubscriptionOffer>()
      if (!this.offersConfig) {
        this.offersConfig = await this.remoteConfig.getObject<SubscriptionOffersConfig>("offersConfig")
      }
      const session = this.sessionResources.session.value()
      const lead = this.leadResources.currentLead.value()
      const age = Number(this.subscriptionSearchActions.searchForm.controls.age.value)

      this.isComplete = isComplete(session, age, lead?.sex, lead?.subscription?.paid === true)

      if(session?.authorizeSubstitutes && age && lead?.sex && this.isComplete && !isForbiddenAgedClient(session, age) && this.offers.length == 0) {
        this.offers.push(convertToObject<SubscriptionOffer>(this.offersConfig.freeOffer, SubscriptionOffer))
      }
      if(this.offers.length <= 1) {
        if (leadDTO.sex == 'F') {
          this.offers = this.offers.concat(this.offersConfig.womanOffers.map(offer => convertToObject<SubscriptionOffer>(offer, SubscriptionOffer)))
        } else {
          this.offers = this.offers.concat(this.offersConfig.manOffers.map(offer => convertToObject<SubscriptionOffer>(offer, SubscriptionOffer)))
        }
      }
      this.subscriptionSearchActions.searchForm.controls.offer.setValue(leadDTO.offer?.code ?? this.offers[0].code)
    }
  }


}
